import {
  Button,
  Modal,
  toast,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { GitMerge, Loader2 } from "lucide-react";
import { useState } from "react";
import type { CatalogProductListItem } from "@/modules/catalog/presentation/api/client";
import { catalogApi } from "@/modules/catalog/presentation/api/client";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { useMergeCatalogProducts } from "../api/queries";

const PRODUCT_PAGE_SIZE = 20;

type CatalogMergeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceProductId: string;
  sourceProductName: string;
  onMerged?: (targetProductId: string) => void;
};

function productLookupQueryFn(params: {
  search: string;
  pageParam: number;
}): Promise<{ items: CatalogProductListItem[]; nextPage: number | null }> {
  const { search, pageParam } = params;
  return catalogApi
    .listProducts({
      limit: PRODUCT_PAGE_SIZE,
      offset: (pageParam - 1) * PRODUCT_PAGE_SIZE,
      search: search.trim() || undefined,
      status: "active",
    })
    .then((res) => ({
      items: res.data,
      nextPage:
        res.meta.offset + res.data.length < res.meta.total
          ? pageParam + 1
          : null,
    }));
}

async function productPreloadQueryFn(
  id: string,
): Promise<CatalogProductListItem | null> {
  if (!id) return null;
  try {
    const p = await catalogApi.getProduct(id);
    return {
      id: p.id,
      name: p.name,
      type: p.type,
      status: p.status,
      categoryId: p.categoryId,
      brandId: p.brandId,
      categoryName: p.categoryName,
      brandName: p.brandName,
      thumbnailUrl: null,
      updatedAt: p.updatedAt,
    };
  } catch {
    return null;
  }
}

export function CatalogMergeModal({
  open,
  onOpenChange,
  sourceProductId,
  sourceProductName,
  onMerged,
}: CatalogMergeModalProps) {
  const [targetProductId, setTargetProductId] = useState("");
  const [confirmStep, setConfirmStep] = useState<0 | 1>(0);
  const mergeMutation = useMergeCatalogProducts();

  const previewQuery = useQuery({
    queryKey: [
      "catalog",
      "merge-preview",
      sourceProductId,
      targetProductId,
    ],
    queryFn: () =>
      catalogApi.previewProductMerge({
        sourceProductId,
        targetProductId,
      }),
    enabled:
      open && Boolean(targetProductId) && targetProductId !== sourceProductId,
  });

  const handleClose = () => {
    setTargetProductId("");
    setConfirmStep(0);
    onOpenChange(false);
  };

  const handleMerge = async () => {
    if (!targetProductId) return;
    try {
      const result = await mergeMutation.mutateAsync({
        sourceProductId,
        targetProductId,
      });
      toast.success(
        `ລວມສຳເລັດ — SKU ລວມ ${result.mergedSkuCount}, ຍ້າຍ ${result.movedSkuCount}, offers ${result.shopOfferCount}`,
      );
      handleClose();
      onMerged?.(result.targetProductId);
    } catch {
      toast.error("ລວມສິນຄ້າລົ້ມເຫຼວ");
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button
        type="button"
        variant="outline"
        onClick={handleClose}
        disabled={mergeMutation.isPending}
      >
        ຍົກເລີກ
      </Button>
      {confirmStep === 0 ? (
        <Button
          type="button"
          disabled={!targetProductId || previewQuery.isFetching}
          onClick={() => setConfirmStep(1)}
        >
          ຕໍ່ໄປ
        </Button>
      ) : (
        <Button
          type="button"
          className="gap-1"
          disabled={!targetProductId || mergeMutation.isPending}
          onClick={() => void handleMerge()}
        >
          {mergeMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <GitMerge className="size-4" />
          )}
          ຢືນຢັນລວມ
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (!next ? handleClose() : undefined)}
      title="ລວມ catalog product"
      description={`ຍ້າຍ SKU, offers, fitment ຈາກ "${sourceProductName}" ໄປ master ເປົ້າໝາຍ ແລ້ວ archive ຕົ້ນທາງ`}
      size="lg"
      footer={footer}
    >
      <div className="space-y-4">
        <div className="rounded-lg border bg-muted/20 p-3 text-sm">
          <p className="font-medium">ຕົ້ນທາງ (ຈະ archive)</p>
          <p className="text-muted-foreground">{sourceProductName}</p>
        </div>

        <div className="space-y-2">
          <p className="font-medium text-sm">Master ເປົ້າໝາຍ</p>
          <InfiniteCombobox<CatalogProductListItem>
            queryKey={["catalog", "products", "merge-target"]}
            queryFn={productLookupQueryFn}
            preloadQueryFn={productPreloadQueryFn}
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            getDisabled={(item) => item.id === sourceProductId}
            value={targetProductId}
            onValueChange={(id) => {
              setTargetProductId(id);
              setConfirmStep(0);
            }}
            placeholder="ຄົ້ນຫາສິນຄ້າ master..."
          />
        </div>

        {previewQuery.isFetching ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="size-4 animate-spin" />
            ກຳລັງຄິດໄລ່ preview...
          </div>
        ) : null}

        {previewQuery.data ? (
          <div className="grid gap-2 rounded-lg border p-3 text-sm sm:grid-cols-2">
            <p>SKU ຕົ້ນທາງ: {previewQuery.data.sourceSkuCount}</p>
            <p>SKU ເປົ້າໝາຍ: {previewQuery.data.targetSkuCount}</p>
            <p>Offers ທີ່ຍ້າຍ: {previewQuery.data.shopOfferCount}</p>
            <p>Fitments ທີ່ຍ້າຍ: {previewQuery.data.fitmentCount}</p>
          </div>
        ) : null}

        {confirmStep === 1 ? (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-amber-900 text-sm dark:text-amber-100">
            ຢືນຢັນອີກຄັ້ງ: ຈະ archive product ຕົ້ນທາງ ແລະ ຍ້າຍຂໍ້ມູນທັງໝົດໄປ master ເປົ້າໝາຍ
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
